import { createContext, useContext, useState, useEffect, useCallback } from 'react';

export const THEMES = {
  obsidian: {
    dark: true,
    name: 'Obsidian',
    description: 'Deep dark — easy on the eyes for long sessions',
    preview: ['#0a0f1a', '#131c2e', '#3b82f6'],
    headerBg: 'linear-gradient(135deg, #060d1a 0%, #0d1626 100%)',
    headerBorder: '#1a2744',
    navBg: '#0d1626',
    navBorder: '#1a2744',
    navActive: '#3b82f6',
    navActiveText: '#fff',
    navText: '#7c8fad',
    navHover: '#141e30',
    accent: '#3b82f6',
    accentHover: '#2563eb',
    bodyBg: '#060d1a',
    cardBg: '#0d1626',
    cardBorder: '#1a2744',
    text: '#f1f5f9',
    textMuted: '#94a3b8',
    border: '#1a2744',
    shadow: 'rgba(0,0,0,0.4)',
    indicator: 'linear-gradient(90deg, #3b82f6, #6366f1)',
  },
  arctic: {
    dark: false,
    name: 'Arctic',
    description: 'Clean white with cool blue + orange accents',
    preview: ['#f8fafc', '#ffffff', '#0ea5e9'],
    headerBg: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    headerBorder: '#334155',
    navBg: '#ffffff',
    navBorder: '#e2e8f0',
    navActive: '#0f172a',
    navActiveText: '#0f172a',
    navText: '#64748b',
    navHover: '#f8fafc',
    accent: '#0ea5e9',
    accentHover: '#0284c7',
    bodyBg: '#f1f5f9',
    cardBg: '#ffffff',
    cardBorder: '#e2e8f0',
    text: '#0f172a',
    textMuted: '#64748b',
    border: '#e2e8f0',
    shadow: 'rgba(0,0,0,0.08)',
    indicator: 'linear-gradient(90deg, #0ea5e9, #f97316)',
  },
  midnight: {
    dark: true,
    name: 'Midnight',
    description: 'Navy blue — professional and focused',
    preview: ['#0f1f3d', '#1a3260', '#6366f1'],
    headerBg: 'linear-gradient(135deg, #060f25 0%, #0f1f3d 100%)',
    headerBorder: '#1a3260',
    navBg: '#0f1f3d',
    navBorder: '#1a3260',
    navActive: '#6366f1',
    navActiveText: '#fff',
    navText: '#8ba3c7',
    navHover: '#162547',
    accent: '#6366f1',
    accentHover: '#4f46e5',
    bodyBg: '#060f25',
    cardBg: '#0f1f3d',
    cardBorder: '#1a3260',
    text: '#f1f5f9',
    textMuted: '#8ba3c7',
    border: '#1a3260',
    shadow: 'rgba(0,0,0,0.4)',
    indicator: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
  },
  emerald: {
    dark: true,
    name: 'Emerald',
    description: 'Dark green — finance and trading aesthetic',
    preview: ['#0a1a12', '#0f2318', '#10b981'],
    headerBg: 'linear-gradient(135deg, #051009 0%, #0a1a12 100%)',
    headerBorder: '#0f3320',
    navBg: '#0a1a12',
    navBorder: '#0f3320',
    navActive: '#10b981',
    navActiveText: '#fff',
    navText: '#5a9e7a',
    navHover: '#0d2016',
    accent: '#10b981',
    accentHover: '#059669',
    bodyBg: '#051009',
    cardBg: '#0a1a12',
    cardBorder: '#0f3320',
    text: '#ecfdf5',
    textMuted: '#6aaf8a',
    border: '#0f3320',
    shadow: 'rgba(0,0,0,0.4)',
    indicator: 'linear-gradient(90deg, #10b981, #34d399)',
  },
  slate: {
    dark: false,
    name: 'Slate',
    description: 'Warm grey — minimal and distraction-free',
    preview: ['#f8f7f5', '#ffffff', '#1e293b'],
    headerBg: 'linear-gradient(135deg, #1c2333 0%, #2d3748 100%)',
    headerBorder: '#374151',
    navBg: '#f8f7f5',
    navBorder: '#e5e1dc',
    navActive: '#1e293b',
    navActiveText: '#1e293b',
    navText: '#6b7280',
    navHover: '#f0ede8',
    accent: '#f59e0b',
    accentHover: '#d97706',
    bodyBg: '#f0ede8',
    cardBg: '#ffffff',
    cardBorder: '#e5e1dc',
    text: '#1e293b',
    textMuted: '#6b7280',
    border: '#e5e1dc',
    shadow: 'rgba(0,0,0,0.06)',
    indicator: 'linear-gradient(90deg, #f59e0b, #f97316)',
  },
  rose: {
    dark: true,
    name: 'Rose',
    description: 'Warm rose — modern and energetic',
    preview: ['#1a0a0f', '#2d1018', '#f43f5e'],
    headerBg: 'linear-gradient(135deg, #130509 0%, #1f0a10 100%)',
    headerBorder: '#3d1020',
    navBg: '#1a0a0f',
    navBorder: '#3d1020',
    navActive: '#f43f5e',
    navActiveText: '#fff',
    navText: '#9a6070',
    navHover: '#220d14',
    accent: '#f43f5e',
    accentHover: '#e11d48',
    bodyBg: '#130509',
    cardBg: '#1a0a0f',
    cardBorder: '#3d1020',
    text: '#fff1f5',
    textMuted: '#b07888',
    border: '#3d1020',
    shadow: 'rgba(0,0,0,0.4)',
    indicator: 'linear-gradient(90deg, #f43f5e, #fb7185)',
  },
  onyx: {
    dark: true,
    name: 'Onyx',
    description: 'Pure black with vibrant orange — ultra high contrast',
    preview: ['#000000', '#111111', '#f97316'],
    headerBg: 'linear-gradient(135deg, #000000 0%, #0a0a0a 100%)',
    headerBorder: '#222222',
    navBg: '#0a0a0a',
    navBorder: '#222222',
    navActive: '#f97316',
    navActiveText: '#fff',
    navText: '#888888',
    navHover: '#141414',
    accent: '#f97316',
    accentHover: '#ea6c0a',
    bodyBg: '#000000',
    cardBg: '#111111',
    cardBorder: '#222222',
    text: '#ffffff',
    textMuted: '#999999',
    border: '#222222',
    shadow: 'rgba(0,0,0,0.6)',
    indicator: 'linear-gradient(90deg, #f97316, #fbbf24)',
  },
  solar: {
    dark: false,
    name: 'Solar',
    description: 'Warm cream — bright and easy to read',
    preview: ['#fffbf0', '#ffffff', '#d97706'],
    headerBg: 'linear-gradient(135deg, #1c1400 0%, #2d2000 100%)',
    headerBorder: '#3d2e00',
    navBg: '#fffbf0',
    navBorder: '#f0e6c8',
    navActive: '#92400e',
    navActiveText: '#92400e',
    navText: '#a16207',
    navHover: '#fef3c7',
    accent: '#d97706',
    accentHover: '#b45309',
    bodyBg: '#fef9ec',
    cardBg: '#fffbf0',
    cardBorder: '#f0e6c8',
    text: '#1c1400',
    textMuted: '#92400e',
    border: '#f0e6c8',
    shadow: 'rgba(0,0,0,0.06)',
    indicator: 'linear-gradient(90deg, #d97706, #f97316)',
  },
  neon: {
    dark: true,
    name: 'Neon',
    description: 'Cyberpunk black with electric cyan and magenta',
    preview: ['#05060a', '#0d1117', '#22d3ee'],
    headerBg: 'linear-gradient(135deg, #05060a 0%, #0d1117 100%)',
    headerBorder: '#1f3a4d',
    navBg: '#0a0d14',
    navBorder: '#1f3a4d',
    navActive: '#22d3ee',
    navActiveText: '#05060a',
    navText: '#6b8aa0',
    navHover: '#101725',
    accent: '#22d3ee',
    accentHover: '#06b6d4',
    bodyBg: '#05060a',
    cardBg: '#0d1117',
    cardBorder: '#1f3a4d',
    text: '#e6fbff',
    textMuted: '#7fa8bd',
    border: '#1f3a4d',
    shadow: 'rgba(34,211,238,0.16)',
    indicator: 'linear-gradient(90deg, #22d3ee, #e879f9)',
  },
  synthwave: {
    dark: true,
    name: 'Synthwave',
    description: 'Retro purple and hot pink — 80s sunset',
    preview: ['#150d2b', '#211540', '#f472b6'],
    headerBg: 'linear-gradient(135deg, #0f0820 0%, #2a1550 100%)',
    headerBorder: '#4c2a7a',
    navBg: '#1a1035',
    navBorder: '#4c2a7a',
    navActive: '#f472b6',
    navActiveText: '#fff',
    navText: '#a78bc9',
    navHover: '#241547',
    accent: '#f472b6',
    accentHover: '#ec4899',
    bodyBg: '#0f0820',
    cardBg: '#1a1035',
    cardBorder: '#4c2a7a',
    text: '#f5ecff',
    textMuted: '#b49ad4',
    border: '#4c2a7a',
    shadow: 'rgba(244,114,182,0.18)',
    indicator: 'linear-gradient(90deg, #f472b6, #818cf8)',
  },
  matrix: {
    dark: true,
    name: 'Matrix',
    description: 'Terminal black with bright phosphor green',
    preview: ['#000600', '#04140a', '#4ade80'],
    headerBg: 'linear-gradient(135deg, #000400 0%, #04140a 100%)',
    headerBorder: '#125c28',
    navBg: '#04140a',
    navBorder: '#125c28',
    navActive: '#4ade80',
    navActiveText: '#00170a',
    navText: '#4f8f63',
    navHover: '#07200f',
    accent: '#4ade80',
    accentHover: '#22c55e',
    bodyBg: '#000600',
    cardBg: '#04140a',
    cardBorder: '#125c28',
    text: '#dcffe6',
    textMuted: '#6cbe86',
    border: '#125c28',
    shadow: 'rgba(74,222,128,0.16)',
    indicator: 'linear-gradient(90deg, #4ade80, #a3e635)',
  },
  ultraviolet: {
    dark: true,
    name: 'Ultraviolet',
    description: 'Deep indigo with vivid violet glow',
    preview: ['#0a0620', '#141034', '#a855f7'],
    headerBg: 'linear-gradient(135deg, #070418 0%, #191045 100%)',
    headerBorder: '#3b2a7a',
    navBg: '#120d30',
    navBorder: '#3b2a7a',
    navActive: '#a855f7',
    navActiveText: '#fff',
    navText: '#8f86c4',
    navHover: '#1a1340',
    accent: '#a855f7',
    accentHover: '#9333ea',
    bodyBg: '#070418',
    cardBg: '#120d30',
    cardBorder: '#3b2a7a',
    text: '#f3eaff',
    textMuted: '#a99cd6',
    border: '#3b2a7a',
    shadow: 'rgba(168,85,247,0.18)',
    indicator: 'linear-gradient(90deg, #a855f7, #22d3ee)',
  },
  custom: {
    dark: true,
    name: 'Custom',
    description: 'Your own colours — fully personalised',
    preview: ['#1a1a2e', '#16213e', '#e94560'],
    headerBg: 'linear-gradient(135deg, #0d0d1a 0%, #1a1a2e 100%)',
    headerBorder: '#2a2a4a',
    navBg: '#16213e',
    navBorder: '#2a2a4a',
    navActive: '#e94560',
    navActiveText: '#fff',
    navText: '#8888aa',
    navHover: '#1e2a4a',
    accent: '#e94560',
    accentHover: '#c73652',
    bodyBg: '#0d0d1a',
    cardBg: '#16213e',
    cardBorder: '#2a2a4a',
    text: '#f0f0ff',
    textMuted: '#9999bb',
    border: '#2a2a4a',
    shadow: 'rgba(0,0,0,0.4)',
    indicator: 'linear-gradient(90deg, #e94560, #a855f7)',
  },
};

// Patterns are drawn in white on dark themes and black on light ones — a fixed
// white overlay was invisible on Arctic, Slate and Solar. `value` takes the ink
// colour so the same pattern works on any background.
export const BACKGROUNDS = {
  none:    { name: 'None',        value: () => null },
  dots:    { name: 'Dot Grid',    value: (c) => `radial-gradient(circle, ${c} 1px, transparent 1px)`, size: '24px 24px' },
  grid:    { name: 'Grid',        value: (c) => `linear-gradient(${c} 1px, transparent 1px), linear-gradient(90deg, ${c} 1px, transparent 1px)`, size: '32px 32px' },
  circuit: { name: 'Circuit',     value: (c) => `repeating-linear-gradient(0deg, transparent, transparent 40px, ${c} 40px, ${c} 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, ${c} 40px, ${c} 41px)`, size: null },
  noise:   { name: 'Subtle Noise', value: () => 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.03\'/%3E%3C/svg%3E")', size: '200px 200px' },
};

// Ink for the pattern overlay: light themes need dark lines, dark themes light ones.
export const patternInk = (isDark) => isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.07)';

// Relative luminance — used for custom themes, where the user picks the colours.
export function isDarkColor(hex) {
  const raw = String(hex || '').trim().replace('#', '');
  const full = raw.length === 3 ? raw.split('').map(c => c + c).join('') : raw;
  if (!/^[0-9a-f]{6}$/i.test(full)) return true;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) < 128;
}

export const CUSTOM_THEME_FIELDS = [
  { key: 'bodyBg',      label: 'Page Background',    group: 'backgrounds' },
  { key: 'cardBg',      label: 'Card Background',     group: 'backgrounds' },
  { key: 'navBg',       label: 'Sidebar Background',  group: 'backgrounds' },
  { key: 'headerStart', label: 'Header Gradient (from)', group: 'header' },
  { key: 'headerEnd',   label: 'Header Gradient (to)',   group: 'header' },
  { key: 'accent',      label: 'Accent / Buttons',    group: 'colours' },
  { key: 'text',        label: 'Primary Text',        group: 'colours' },
  { key: 'textMuted',   label: 'Secondary Text',      group: 'colours' },
  { key: 'border',      label: 'Borders',             group: 'colours' },
];

function buildCustomTheme(base, overrides) {
  if (!overrides || !Object.keys(overrides).length) return base;
  const t = { ...base };
  if (overrides.bodyBg)    { t.bodyBg = overrides.bodyBg; }
  if (overrides.cardBg)    { t.cardBg = overrides.cardBg; }
  if (overrides.navBg)     { t.navBg = overrides.navBg; t.navHover = overrides.navBg; }
  if (overrides.headerStart || overrides.headerEnd) {
    const s = overrides.headerStart || base.headerBg.match(/#[0-9a-f]{3,8}/i)?.[0] || '#0d0d1a';
    const e = overrides.headerEnd   || base.headerBg.match(/#[0-9a-f]{3,8}/ig)?.[1]  || '#1a1a2e';
    t.headerBg = `linear-gradient(135deg, ${s} 0%, ${e} 100%)`;
    t.headerBorder = overrides.border || s;
  }
  if (overrides.accent)    { t.accent = overrides.accent; t.accentHover = overrides.accent; t.navActive = overrides.accent; t.indicator = `linear-gradient(90deg, ${overrides.accent}, ${overrides.accent}cc)`; }
  if (overrides.text)      { t.text = overrides.text; t.navActiveText = overrides.text; }
  if (overrides.textMuted) { t.textMuted = overrides.textMuted; t.navText = overrides.textMuted; }
  if (overrides.border)    { t.border = overrides.border; t.cardBorder = overrides.border; t.navBorder = overrides.border; if (!overrides.headerStart && !overrides.headerEnd) t.headerBorder = overrides.border; }
  return t;
}

const PREF_KEY = 'crm_user_prefs';

function loadPrefs(username) {
  try {
    const raw = localStorage.getItem(`${PREF_KEY}_${username}`);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function savePrefs(username, prefs) {
  try { localStorage.setItem(`${PREF_KEY}_${username}`, JSON.stringify(prefs)); } catch {}
}

const ThemeContext = createContext(null);

export function ThemeProvider({ user, children }) {
  const username = user?.username || 'default';
  const prefs = loadPrefs(username);

  const [themeKey,      setThemeKey]      = useState(prefs.theme      || 'arctic');
  const [bgKey,         setBgKey]         = useState(prefs.bg         || 'none');
  const [customBgUrl,   setCustomBgUrl]   = useState(prefs.customBg   || '');
  const [soundsOn,      setSoundsOn]      = useState(prefs.sounds     !== false);
  const [notifsOn,      setNotifsOn]      = useState(prefs.notifs     !== false);
  const [customColors,  setCustomColors]  = useState(prefs.customColors || {});

  const baseTheme = THEMES[themeKey] || THEMES.arctic;
  const theme = themeKey === 'custom' ? buildCustomTheme(baseTheme, customColors) : baseTheme;
  // Custom themes are whatever the user made them, so measure rather than assume.
  const isDark = themeKey === 'custom' ? isDarkColor(theme.bodyBg) : !!theme.dark;

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--crm-accent',            theme.accent);
    root.style.setProperty('--crm-accent-hover',      theme.accentHover);
    root.style.setProperty('--crm-body-bg',           theme.bodyBg);
    root.style.setProperty('--crm-card-bg',           theme.cardBg);
    root.style.setProperty('--crm-card-border',       theme.cardBorder);
    root.style.setProperty('--crm-text',              theme.text);
    root.style.setProperty('--crm-text-muted',        theme.textMuted);
    root.style.setProperty('--crm-border',            theme.border);
    root.style.setProperty('--crm-nav-bg',            theme.navBg);
    root.style.setProperty('--crm-nav-active',        theme.navActive);
    root.style.setProperty('--crm-nav-active-text',   theme.navActiveText);
    root.style.setProperty('--crm-nav-text',          theme.navText);
    root.style.setProperty('--crm-header-bg',         theme.headerBg);
    root.style.setProperty('--crm-indicator',          theme.indicator);
    // These were read by components but never defined, so they resolved to nothing.
    root.style.setProperty('--crm-text-secondary',    theme.textMuted);
    root.style.setProperty('--crm-nav-border',        theme.navBorder);
    root.style.setProperty('--crm-nav-hover',         theme.navHover);
    root.style.setProperty('--crm-header-border',     theme.headerBorder);
    root.style.setProperty('--crm-shadow',            theme.shadow);
    // Measure the nav colour rather than matching a hard-coded list of hexes,
    // which missed custom themes entirely.
    const isLightNav = !isDarkColor(theme.navBg);
    root.style.setProperty('--crm-modal-header-bg',   isLightNav ? theme.navActive : theme.navBg);
    root.style.setProperty('--crm-modal-header-text', isLightNav ? '#fff' : theme.navActiveText);
    root.setAttribute('data-crm-theme', isDark ? 'dark' : 'light');
  }, [theme, isDark]);

  const update = useCallback((key, val, setter) => {
    setter(val);
    const current = loadPrefs(username);
    savePrefs(username, { ...current, [key]: val });
  }, [username]);

  const updateCustomColor = useCallback((colorKey, value) => {
    const next = { ...customColors, [colorKey]: value };
    setCustomColors(next);
    const current = loadPrefs(username);
    savePrefs(username, { ...current, customColors: next });
  }, [customColors, username]);

  const resetCustomColors = useCallback(() => {
    setCustomColors({});
    const current = loadPrefs(username);
    savePrefs(username, { ...current, customColors: {} });
  }, [username]);

  const ctx = {
    theme, themeKey, isDark, setThemeKey: v => update('theme', v, setThemeKey),
    bgKey,  setBgKey:  v => update('bg',     v, setBgKey),
    customBgUrl, setCustomBgUrl: v => update('customBg', v, setCustomBgUrl),
    soundsOn, setSoundsOn: v => update('sounds', v, setSoundsOn),
    notifsOn, setNotifsOn: v => update('notifs', v, setNotifsOn),
    customColors, updateCustomColor, resetCustomColors,
  };

  return <ThemeContext.Provider value={ctx}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider');
  return ctx;
}
